import { Button, Chip, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAgamiRoutes } from '../hooks/useAgamiRoutes'
import { SubmissionEntry } from '../utils/helpers/submissions.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

const OUTCOME_COLORS: Record<SubmissionEntry['outcome'], colors> = {
  pending: 'amber',
  approved: 'green',
  rejected: 'red',
}

/**
 * One proposal of the history (12.2).
 *
 * The revision sign is the element of most value on the page — it says how much
 * moderation intervenes on what visitors propose — so it is a chip in the row,
 * never a tooltip. The second title appears ONLY when it differs: otherwise it
 * is noise. A rejected proposal shows the original title alone, with no link:
 * the spot no longer exists.
 *
 * ⚠️ The one action is OPEN, on pending proposals. No rejection from here, by
 * decision: rejecting is irreversible (05.4's hard delete), and whoever clears
 * the queue does it from the Bozze section, where the duplicates are grouped
 * and the confirmation is per group. This is the analysis panel; deleting from
 * a statistics page is an accident waiting to happen.
 */
const SubmissionRow: React.FC<{ submission: SubmissionEntry }> = ({ submission }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { agamiFormPath } = useAgamiRoutes()
  const { darkMode } = useDarkMode()

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString(i18n.language)

  const finalTitle = submission.titleAtModeration ?? submission.currentTitle
  const showFinalTitle = submission.revised === true && finalTitle && finalTitle !== submission.originalTitle

  const proposedLine = submission.submitter?.name
    ? t('submissions.proposedBy', {
        name: submission.submitter.name,
        date: formatDate(submission.submittedAt),
      })
    : t('submissions.proposedOn', { date: formatDate(submission.submittedAt) })

  return (
    <div
      className="flex flex-col md:flex-row md:items-center justify-between gap-2 py-3 border-b border-gray-200 dark:border-base-gray-600"
      data-testclass="submission-row"
      data-outcome={submission.outcome}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Typography className="font-semibold" data-testclass="submission-original-title">
            « {submission.originalTitle} »
          </Typography>
          {showFinalTitle && (
            <>
              <span aria-hidden="true">→</span>
              <Typography className="font-semibold" data-testclass="submission-final-title">
                « {finalTitle} »
              </Typography>
            </>
          )}
          {submission.revised === true && (
            <Chip
              size="sm"
              variant="ghost"
              color="blue"
              value={t('submissions.revisedChip')}
              data-testclass="submission-revised-chip"
            />
          )}
        </div>
        <Typography variant="small" className="text-gray-600 dark:text-base-gray-500">
          <Chip
            size="sm"
            variant="ghost"
            color={OUTCOME_COLORS[submission.outcome]}
            value={t(`submissions.outcome.${submission.outcome}`)}
            className="inline-block mr-2 align-middle"
            data-testclass="submission-outcome-chip"
          />
          {proposedLine}
          {submission.moderatedAt &&
            ` · ${t('submissions.moderatedOn', { date: formatDate(submission.moderatedAt) })}`}
        </Typography>
      </div>

      {submission.outcome === 'pending' && submission.spotId && (
        <Button
          size="sm"
          variant="outlined"
          color={darkMode ? 'white' : ('primary' as colors)}
          className="self-start md:self-center"
          onClick={() => navigate(agamiFormPath(submission.spotId!))}
          data-testclass="submission-open-btn"
        >
          {t('submissions.open')}
        </Button>
      )}
    </div>
  )
}

export default SubmissionRow
